import { useContext, useReducer, createContext, useCallback } from "react";
import { useRequest } from "../../services/request";

const Context = createContext();

export function useReservation() {
  return useContext(Context);
}

const initState = {
  list: [],
  selected: null,
  companyId: null,
  count: 0,
  page: 1,
  perPage: 10,
  totalSize: 0,
  status: "idle",
  error: null,
};
const reducer = (state, action) => {
  switch (action.type) {
    case "set_list":
      return { ...state, list: [...action.payload] };

    case "set_selected":
      return { ...state, selected: { ...action.payload } };

    case "set_count":
      return { ...state, count: action.payload };
    case "set_CompanyId":
      return { ...state, companyId: action.payload };
    case "set_length":
      return { ...state, length: action.payload };
    case "add":
      const newList = [{ ...action.payload }, ...state.list];
      return { ...state, list: newList };
    case "edit":
      const modified = state.list.map((p) =>
        p.id === action.payload.id ? { ...p, ...action.payload } : p
      );
      return { ...state, list: modified };
    case "delete":
      const filtered = state.list.filter((p) => p.id !== action.payload);
      return { ...state, list: filtered };
    case "status":
      return { ...state, status: action.payload };
    case "set_totalSize":
      return { ...state, totalSize: action.payload };
    case "set_page":
      return { ...state, page: action.payload };
    case "set_per_page":
      return { ...state, perPage: action.payload };
    case "set_listSubCompanies":
      return { ...state, listSubCompanies: [...action.payload] };

    default:
      throw new Error(`Invalid dispatch type: ${action.type}`);
  }
};

export default function ReservationProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initState);
  const req = useRequest();
  const fetchList = useCallback(
    async (data, page, perPage) => {
      dispatch({ type: "status", payload: "fetching" });
      let headersData = {
        pageNumber: page,
      };
      if (perPage) headersData.pageSize = perPage;
      if (data.fromDate) headersData.fromDate = data.fromDate;
      if (data.toDate) headersData.toDate = data.toDate;

      const resData = await req(
        `SiteReservation/List/${data?.siteId}`,
        null,
        {},
        true,
        headersData
      );
      dispatch({ type: "status", payload: `idle` });
      dispatch({ type: "set_list", payload: resData.data?.reservations || [] });
      dispatch({ type: "set_per_page", payload: resData.data?.pageSize });
      dispatch({ type: "set_count", payload: resData.data?.totalPages });
      dispatch({ type: "set_length", payload: resData.data?.totalSize });
    },
    [req]
  );
  const create = useCallback(
    async (data) => {
      return new Promise(async (resolve, reject) => {
        try {
          const newData = { ...data };
          const resData = await req(
            "SiteReservation/Add",
            newData,
            { method: "POST" },
            true
          );
          dispatch({ type: "add", payload: resData.data });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req]
  );
  const remove = useCallback(
    async (id) => {
      return new Promise(async (resolve, reject) => {
        try {
          const resData = await req(
            `SiteReservation/Delete/${id}`,
            null,
            { method: "DELETE" },
            true
          );
          dispatch({ type: "delete", payload: id });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req]
  );
  const edit = useCallback(
    async (id, data) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `creating` });
        try {
          const resData = await req(
            `SiteReservation/Update/${id}`,
            data,
            { method: "PUT" },
            true
          );
          dispatch({ type: "edit", payload: resData });
          resolve(resData);
        } catch (e) {
          reject(e);
        } finally {
          dispatch({ type: "status", payload: `idle` });
        }
      });
    },
    [req]
  );

  const fetchOne = useCallback(
    (id) => {
      return new Promise(async (resolve, reject) => {
        dispatch({ type: "status", payload: `fetching` });
        const resData = await req(`SiteReservation/Info/${id}`, null, {}, true);
        dispatch({ type: "set_selected", payload: resData.data });
        dispatch({ type: "status", payload: `idle` });
        resolve(resData);
      });
    },
    [req]
  );

  return (
    <Context.Provider
      value={{
        state,
        fetchList,
        fetchOne,
        edit,
        create,
        remove,
        dispatch,
      }}
    >
      {children}
    </Context.Provider>
  );
}
